import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Image } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { getPaymentMethod, updatePaymentMethod } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const EditPaymentMethod = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [validated, setValidated] = useState(false);
    const [formData, setFormData] = useState({
        accountHolderName: '',
        bankName: '',
        accountNumber: '',
        ifscCode: '',
        upiId: '',
        upiQrCode: null
    });
    const [qrPreview, setQrPreview] = useState('');
    const [initialQrCode, setInitialQrCode] = useState('');

    useEffect(() => {
        fetchPaymentMethod();
    }, [id]);

    const fetchPaymentMethod = async () => {
        try {
            setLoading(true);
            const response = await getPaymentMethod(id);
            
            if (response.success && response.data) {
                const { accountHolderName, bankName, accountNumber, ifscCode, upiId, upiQrCode } = response.data;
                setFormData({
                    accountHolderName: accountHolderName || '',
                    bankName: bankName || '',
                    accountNumber: accountNumber || '',
                    ifscCode: ifscCode || '',
                    upiId: upiId || '',
                    upiQrCode: null
                });

                if (upiQrCode) {
                    setInitialQrCode(upiQrCode);
                    setQrPreview(`${process.env.REACT_APP_IMAGE_URL || ''}/${upiQrCode}`);
                }
            } else {
                throw new Error(response.message || 'Failed to fetch payment method details');
            }
        } catch (error) {
            console.error('Error fetching payment method:', error);
            alert(error.message || "Failed to fetch payment method details");
            navigate('/admin/payment-methods');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                upiQrCode: file
            }));
            setQrPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (!form.checkValidity()) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        try {
            setLoading(true);
            
            // Create FormData object for multipart/form-data
            const data = new FormData();
            Object.keys(formData).forEach(key => {
                if (key === 'upiQrCode') {
                    if (formData[key]) {
                        data.append(key, formData[key]);
                    }
                } else {
                    data.append(key, formData[key]);
                }
            });

            const response = await updatePaymentMethod(id, data);
            
            if (response.success) {
                alert(response.message || "Payment method updated successfully");
                navigate('/admin/payment-methods');
            } else {
                throw new Error(response.message || "Failed to update payment method");
            }
        } catch (error) {
            console.error('Error updating payment method:', error);
            alert(error.message || "Failed to update payment method");
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveQrCode = () => {
        setFormData(prev => ({
            ...prev,
            upiQrCode: null
        }));
        setQrPreview('');
        setInitialQrCode('');
    };

    if (loading && !formData.accountHolderName) {
        return (
            <div className="container-xxl">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Edit Payment Method" sidebutton={false} />

            <div className="row">
                <div className="col-xl-12 col-lg-12">
                    <Card>
                        <Card.Header>
                            <Card.Title>Edit Payment Method</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <strong>Account Holder Name</strong>
                                                <span className="text-danger ms-1">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="accountHolderName"
                                                value={formData.accountHolderName}
                                                onChange={handleChange}
                                                placeholder="Enter account holder name"
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter account holder name
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <strong>Bank Name</strong>
                                                <span className="text-danger ms-1">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="bankName"
                                                value={formData.bankName}
                                                onChange={handleChange}
                                                placeholder="Enter bank name"
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter bank name
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <strong>Account Number</strong>
                                                <span className="text-danger ms-1">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="accountNumber"
                                                value={formData.accountNumber}
                                                onChange={handleChange}
                                                placeholder="Enter account number"
                                                pattern="[0-9]{9,18}"
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter a valid account number (9-18 digits)
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <strong>IFSC Code</strong>
                                                <span className="text-danger ms-1">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="ifscCode"
                                                value={formData.ifscCode}
                                                onChange={handleChange}
                                                placeholder="Enter IFSC code"
                                                pattern="^[A-Z]{4}0[A-Z0-9]{6}$"
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Please enter a valid IFSC code (e.g., HDFC0001234)
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <strong>UPI ID</strong>
                                                <span className="text-muted ms-2">(Optional)</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="upiId"
                                                value={formData.upiId}
                                                onChange={handleChange}
                                                placeholder="Enter UPI ID"
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                <strong>QR Code</strong>
                                                <span className="text-muted ms-2">(Optional)</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                className="mb-2"
                                            />
                                            {qrPreview && (
                                                <div className="mt-2 position-relative d-inline-block">
                                                    <Image
                                                        src={qrPreview}
                                                        alt="QR Code Preview"
                                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                        thumbnail
                                                    />
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="position-absolute top-0 end-0 m-1"
                                                        onClick={handleRemoveQrCode}
                                                        title="Remove QR Code"
                                                    >
                                                        <i className="fa fa-times"></i>
                                                    </Button>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="mt-3">
                                    <Button 
                                        variant="secondary" 
                                        className="me-2"
                                        onClick={() => navigate('/admin/payment-methods')}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="primary"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="me-2">Updating...</span>
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            </>
                                        ) : (
                                            'Update Payment Method'
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default EditPaymentMethod;

